import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Sparkles, ChevronRight, Clock, Check, Target, Play,
  BookOpen, Calendar, ArrowRight, Zap, Flame
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { NotificationBanner } from '../components/NotificationCenter';

// Daily micro-actions linked to each step of the new journey
const DAILY_ACTIONS = {
  // Phase 1: FONDATION
  vision_revelation: [
    { task: "Reformule ta vision en une phrase simple et mémorable", duration: "10 min" },
    { task: "Note 3 raisons pour lesquelles cette vision compte pour toi", duration: "5 min" },
    { task: "Visualise où tu seras dans 1 an si tu suis cette vision", duration: "10 min" }
  ],
  brand_audit: [
    { task: "Note les 3 points forts de ta marque actuelle", duration: "10 min" },
    { task: "Identifie 2 aspects à améliorer dans ta communication", duration: "10 min" },
    { task: "Définis une priorité pour les prochains jours", duration: "5 min" }
  ],
  // Phase 2: POSITIONNEMENT
  positioning_clarity: [
    { task: "Liste 3 sujets sur lesquels tu veux prendre la parole", duration: "10 min" },
    { task: "Identifie ce qui te différencie de tes concurrents", duration: "10 min" },
    { task: "Écris ta proposition de valeur unique en une phrase", duration: "10 min" }
  ],
  // Phase 3: IDENTITÉ
  vision_identity: [
    { task: "Choisis tes 3 couleurs principales de marque", duration: "10 min" },
    { task: "Mets à jour ta photo de profil avec ton nouveau style", duration: "15 min" },
    { task: "Crée un dossier avec tous tes éléments visuels", duration: "10 min" }
  ],
  brand_visualization: [
    { task: "Crée un moodboard avec 5 images qui représentent ta marque", duration: "15 min" },
    { task: "Choisis une typographie qui reflète ta personnalité", duration: "10 min" },
    { task: "Télécharge tes visuels de marque", duration: "5 min" }
  ],
  // Phase 4: CONTENU
  content_creation: [
    { task: "Écris ton premier post aligné avec ta nouvelle identité", duration: "15 min" },
    { task: "Planifie tes 3 prochains contenus pour cette semaine", duration: "10 min" },
    { task: "Identifie un problème que tu résous pour ton audience", duration: "5 min" }
  ],
  // Phase 5: CROISSANCE
  growth_strategy: [
    { task: "Définis tes 3 canaux de communication prioritaires", duration: "10 min" },
    { task: "Engage avec 5 personnes de ta communauté aujourd'hui", duration: "15 min" },
    { task: "Planifie ta première semaine de publication", duration: "10 min" }
  ],
  // Phase 6: ÉVOLUTION
  brand_evolution: [
    { task: "Relis ton diagnostic et note 3 actions à mettre en place", duration: "10 min" },
    { task: "Connecte-toi avec une autre membre de la communauté", duration: "10 min" },
    { task: "Réfléchis à ta prochaine évolution de marque", duration: "10 min" }
  ],
  // Fallback for legacy steps
  profile_revelation: [
    { task: "Relis ton profil révélé et note 3 traits qui te définissent", duration: "5 min" },
    { task: "Écris une phrase qui résume qui tu es en tant qu'entrepreneure", duration: "10 min" },
    { task: "Identifie une force unique que tu apportes à tes clients", duration: "5 min" }
  ],
  content_revelation: [
    { task: "Liste 3 sujets sur lesquels tu veux prendre la parole", duration: "10 min" },
    { task: "Écris ton premier post aligné avec ta nouvelle direction", duration: "15 min" },
    { task: "Identifie un problème que tu résous pour ton audience", duration: "5 min" }
  ],
  first_action: [
    { task: "Publie ton premier contenu avec ta nouvelle identité", duration: "15 min" },
    { task: "Partage ta transformation avec une personne de confiance", duration: "5 min" },
    { task: "Planifie tes 3 prochains contenus pour cette semaine", duration: "10 min" }
  ],
  mastery: [
    { task: "Explore un nouvel outil dans la suite Vision Lab", duration: "15 min" },
    { task: "Connecte-toi avec une autre membre de la communauté", duration: "10 min" },
    { task: "Réfléchis à ta prochaine évolution de marque", duration: "10 min" }
  ]
};

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [journeyData, setJourneyData] = useState(null);
  const [dailyWord, setDailyWord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailyAction, setDailyAction] = useState(null);
  const [dailyActionCompleted, setDailyActionCompleted] = useState(false);
  const [streakDays, setStreakDays] = useState(0);
  const [disciplinePoints, setDisciplinePoints] = useState(0);
  const [completingAction, setCompletingAction] = useState(false);
  const [expectationsChecked, setExpectationsChecked] = useState(false);

  // Check if initial expectations have been filled
  useEffect(() => {
    const checkExpectations = async () => {
      if (!token || expectationsChecked) return;
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API}/journey/expectations`, { headers });
        
        // If expectations not completed, redirect to journey start
        if (!response.data.completed && !response.data.expectations) {
          navigate('/journey-start');
          return;
        }
        
        setExpectationsChecked(true);
      } catch (error) {
        // If error, assume expectations exist and continue
        setExpectationsChecked(true);
      }
    };

    checkExpectations();
  }, [token, navigate, expectationsChecked]);

  // Redirect to correct step if not completed
  // Only redirect if trying to access dashboard directly
  useEffect(() => {
    if (!user) return;
    
    // Allow dashboard access but show current step info
    // Don't force redirect - let user see their progress
    
    // Only redirect if onboarding not completed (critical)
    if (!user.onboarding_completed) {
      navigate('/onboarding');
      return;
    }
  }, [user, navigate]);

  // Fetch journey progress and daily word
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        const [journeyRes, wordRes, progressRes] = await Promise.all([
          axios.get(`${API}/journey/main-progress`, { headers }),
          axios.get(`${API}/daily-word/today`, { headers }),
          axios.get(`${API}/journey/progress`, { headers })
        ]);
        
        setJourneyData(journeyRes.data);
        setDailyWord(wordRes.data);
        setDailyActionCompleted(progressRes.data.daily_action_completed_today || false);
        setStreakDays(progressRes.data.streak_days || 0);
        setDisciplinePoints(progressRes.data.discipline_points || 0);
        
        // Select daily action based on current step and day
        const currentStepId = journeyRes.data.current_step?.id || 'mastery';
        const actions = DAILY_ACTIONS[currentStepId] || DAILY_ACTIONS.mastery;
        const dayIndex = (wordRes.data?.day || 1) % actions.length;
        setDailyAction(actions[dayIndex]);
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Complete daily action
  const completeDailyAction = async () => {
    if (completingAction || dailyActionCompleted) return;
    
    setCompletingAction(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/journey/complete-daily-action`, {}, { headers });
      
      if (response.data.bonus_earned) {
        setDailyActionCompleted(true);
        setStreakDays(response.data.streak_days);
        setDisciplinePoints(response.data.discipline_points);
        
        // Show encouraging toast with points
        toast.success(
          <div className="flex flex-col">
            <span className="font-medium">+{response.data.bonus_points} points discipline</span>
            <span className="text-sm opacity-80">{response.data.encouragement}</span>
          </div>,
          {
            duration: 5000,
            icon: '🔥'
          }
        );
        
        // Show badge notification if new badge earned
        if (response.data.new_badge) {
          setTimeout(() => {
            toast.success(
              <div className="flex flex-col">
                <span className="font-medium">Badge débloqué : {response.data.new_badge.name} !</span>
                <span className="text-sm opacity-80">{response.data.new_badge.description}</span>
              </div>,
              {
                duration: 6000,
                icon: '🏆'
              }
            );
          }, 2000);
        }
      } else {
        toast.info(t('dashboard.dailyActionAlreadyDone'));
      }
    } catch (error) {
      console.error('Error completing daily action:', error);
      toast.error(t('errors.generic'));
    } finally {
      setCompletingAction(false);
    }
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    const name = user?.name?.split(' ')[0] || 'Belle âme';
    if (hour < 12) return `Bonjour, ${name}`;
    if (hour < 18) return `Bon après-midi, ${name}`;
    return `Bonsoir, ${name}`;
  };

  // Get motivational message based on progress
  const getMotivationalMessage = () => {
    if (!journeyData) return "Prête à avancer ?";
    const { progress_percent, current_step } = journeyData;
    
    if (progress_percent === 100) {
      return "Tu as complété ton parcours. Continue à évoluer.";
    }
    if (progress_percent >= 80) {
      return "Tu y es presque ! Encore un petit effort.";
    }
    if (progress_percent >= 50) {
      return "Tu avances bien. Continue sur ta lancée.";
    }
    return "Chaque pas compte. Avance à ton rythme.";
  };

  if (loading) {
    return (
      <DashboardLayout title="Aujourd'hui">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse text-[#D4AF37]">Chargement...</div>
        </div>
      </DashboardLayout>
    );
  }

  const { current_step, next_step, progress_percent, completed_count, total_steps, is_journey_complete } = journeyData || {};

  return (
    <DashboardLayout title="Aujourd'hui">
      <div className="max-w-3xl mx-auto">
        
        {/* ============ SMART NOTIFICATION ============ */}
        <NotificationBanner />
        
        {/* ============ GREETING ============ */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-2">
            {getGreeting()}
          </h1>
          <p className="text-[#A1A1AA] font-light">
            {getMotivationalMessage()}
          </p>
        </motion.div>

        {/* ============ AUJOURD'HUI - MAIN CARD ============ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-gold p-8 mb-8"
          data-testid="today-card"
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="text-[#D4AF37]" size={18} />
            <p className="text-[#D4AF37] text-xs font-accent tracking-[0.25em]">
              AUJOURD'HUI DANS TON PARCOURS
            </p>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {[...Array(total_steps || 6)].map((_, i) => (
                <div
                  key={i}
                  className={`w-8 h-1 ${
                    i < (completed_count || 0)
                      ? 'bg-[#D4AF37]'
                      : i === (completed_count || 0)
                        ? 'bg-[#D4AF37]/50 animate-pulse'
                        : 'bg-[#27272A]'
                  }`}
                />
              ))}
            </div>
            <span className="text-[#E5C585] text-sm font-serif">
              {completed_count || 0}/{total_steps || 6}
            </span>
          </div>

          {/* Current Step */}
          {current_step && !is_journey_complete ? (
            <div className="mb-8">
              {/* Phase indicator */}
              {current_step.phase_name && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                  <p className="text-[#D4AF37] text-xs font-accent tracking-[0.15em] uppercase">
                    {current_step.phase_name}
                  </p>
                </div>
              )}
              <p className="text-[#71717A] text-xs mb-2">TA PROCHAINE ÉTAPE</p>
              <h2 className="font-serif text-2xl md:text-3xl text-white mb-3">
                {current_step.name}
              </h2>
              <p className="text-[#A1A1AA] font-light mb-4">
                {current_step.description}
              </p>
              {current_step.duration && (
                <div className="flex items-center gap-2 text-[#52525B] text-sm">
                  <Clock size={14} />
                  <span>{current_step.duration}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="mb-8">
              <p className="text-[#71717A] text-xs mb-2">FÉLICITATIONS</p>
              <h2 className="font-serif text-2xl md:text-3xl text-white mb-3">
                Parcours complété 🎉
              </h2>
              <p className="text-[#A1A1AA] font-light">
                Tu as terminé ton parcours de transformation. Explore maintenant les outils avancés.
              </p>
            </div>
          )}

          {/* Main CTA */}
          <Button
            onClick={() => navigate(current_step?.path || '/brand-journey')}
            className="btn-gold w-full py-5 text-base"
            data-testid="continue-journey-btn"
          >
            <Play size={18} className="mr-2" />
            {is_journey_complete ? 'Explorer les outils' : (current_step?.cta || 'Continuer mon parcours')}
            <ChevronRight size={18} className="ml-2" />
          </Button>
        </motion.div>

        {/* ============ MICRO-ACTION DU JOUR ============ */}
        {dailyAction && !is_journey_complete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`card-premium p-6 mb-8 ${dailyActionCompleted ? 'border-[#D4AF37]/30' : ''}`}
            data-testid="daily-action-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap className="text-[#E5C585]" size={16} />
                <p className="text-[#E5C585] text-xs font-accent tracking-[0.2em]">
                  TA MICRO-ACTION DU JOUR
                </p>
              </div>
              <div className="flex items-center gap-4">
                {disciplinePoints > 0 && (
                  <div className="flex items-center gap-1 text-[#A1A1AA] text-xs">
                    <Target size={12} />
                    <span>{disciplinePoints} pts discipline</span>
                  </div>
                )}
                {streakDays > 0 && (
                  <div className="flex items-center gap-1 text-[#D4AF37] text-xs">
                    <Flame size={14} />
                    <span>{streakDays} {streakDays > 1 ? 'jours' : 'jour'}</span>
                  </div>
                )}
              </div>
            </div>
            
            <p className={`text-lg font-light leading-relaxed mb-4 ${dailyActionCompleted ? 'text-[#A1A1AA] line-through' : 'text-white'}`}>
              "{dailyAction.task}"
            </p>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#52525B] text-sm">
                <Clock size={14} />
                <span>{dailyAction.duration}</span>
              </div>
              
              {dailyActionCompleted ? (
                <div className="flex items-center gap-2 text-[#D4AF37] text-sm">
                  <Check size={16} />
                  <span>{t('dashboard.actionDone')}</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <button 
                    className="text-[#71717A] hover:text-[#A1A1AA] text-sm flex items-center gap-1 transition-colors"
                    onClick={() => navigate(current_step?.path || '/brand-journey')}
                  >
                    {t('dashboard.startAction')} <ArrowRight size={14} />
                  </button>
                  <button 
                    onClick={completeDailyAction}
                    disabled={completingAction}
                    className="px-4 py-2 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#E5C585] transition-colors flex items-center gap-2"
                    data-testid="complete-daily-action-btn"
                  >
                    {completingAction ? (
                      <span className="animate-pulse">{t('common.loading')}</span>
                    ) : (
                      <>
                        <Check size={14} />
                        {t('dashboard.markAsDone')}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ============ MOT DU JOUR ============ */}
        {dailyWord && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-premium p-6 mb-8"
            data-testid="daily-word-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="text-[#D4AF37]" size={16} />
              <p className="text-[#D4AF37] text-xs font-accent tracking-[0.2em]">
                TON MOT DU JOUR
              </p>
            </div>
            
            <p className="text-[#E5C585] text-sm mb-2">{dailyWord.faith_word}</p>
            <p className="text-white font-serif text-lg italic leading-relaxed mb-4">
              "{dailyWord.verse_text}"
            </p>
            <p className="text-[#A1A1AA] text-sm font-light">
              {dailyWord.encouragement}
            </p>
          </motion.div>
        )}

        {/* ============ JOURNEY MAP (Compact) ============ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-premium p-6"
          data-testid="journey-overview"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Target className="text-[#71717A]" size={16} />
              <p className="text-[#71717A] text-xs font-accent tracking-[0.2em]">
                TON PARCOURS
              </p>
            </div>
            <button 
              onClick={() => navigate('/brand-journey')}
              className="text-[#52525B] hover:text-[#D4AF37] text-xs transition-colors"
            >
              Voir tout →
            </button>
          </div>

          {/* Compact step list */}
          <div className="space-y-2">
            {journeyData?.all_steps?.map((step, index) => {
              const isCompleted = journeyData.completed_steps.includes(step.id);
              const isCurrent = current_step?.id === step.id;
              
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 py-2 ${
                    isCurrent ? 'text-white' : isCompleted ? 'text-[#71717A]' : 'text-[#3F3F46]'
                  }`}
                >
                  <div className={`w-6 h-6 flex items-center justify-center text-xs border ${
                    isCompleted 
                      ? 'bg-[#D4AF37] border-[#D4AF37] text-black' 
                      : isCurrent 
                        ? 'border-[#D4AF37] text-[#D4AF37]' 
                        : 'border-[#27272A] text-[#52525B]'
                  }`}>
                    {isCompleted ? <Check size={12} /> : index + 1}
                  </div>
                  <span className={`text-sm ${isCurrent ? 'font-medium' : 'font-light'}`}>
                    {step.name}
                  </span>
                  {isCurrent && (
                    <span className="text-[8px] font-accent tracking-wider text-[#D4AF37] px-2 py-0.5 bg-[#D4AF37]/10 ml-auto">
                      EN COURS
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
